/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import type { CustomEventListener } from "@/types";

export class Component<EventSpec> {
    private __listeners: Map<keyof EventSpec, CustomEventListener[]> = new Map();

    /**
     * Destroy instance
     */
    public destroy(): void {
        this.off();
    }

    /**
     * Add event listener for specified event.
     * Returns function to remove event listener.
     */
    public on<K extends keyof EventSpec, E extends EventSpec[K]>(
        eventType: K,
        handler: CustomEventListener<E>
    ) {
        if (!handler) {
            return () => {};
        }

        let listeners = this.__listeners.get(eventType);
        if (!listeners) {
            listeners = [];
            this.__listeners.set(eventType, listeners);
        }

        listeners.push(handler);

        return () => this.off(eventType, handler);
    }

    /**
     * Remove specific or all event listeners for specified event
     */
    public off<K extends keyof EventSpec>(eventType: K, handler?: CustomEventListener): void;
    public off(): void;
    public off<K extends keyof EventSpec>(eventType?: K, handler?: CustomEventListener) {
        if (typeof eventType === 'undefined') {
            return this.__listeners.clear();
        }

        if (typeof handler === 'undefined') {
            this.__listeners.delete(eventType);
            return;
        }

        const listeners = this.__listeners.get(eventType);
        if (listeners) {
            const index = listeners.indexOf(handler);

            if (index > -1) {
                listeners.splice(index, 1);

                if (!listeners.length) {
                    this.__listeners.delete(eventType);
                }
            }
        }
    }

    /**
     * Trigger an event
     */
    public trigger<K extends keyof EventSpec, E extends EventSpec[K]>(eventType: K, data?: E): void;
    public trigger<K extends keyof EventSpec, E extends EventSpec[K]>(event: CustomEvent<E>): void;
    public trigger<K extends keyof EventSpec, E extends EventSpec[K]>(eventType: K | CustomEvent<E>, data?: E) {
        if (!(eventType instanceof CustomEvent)) {
            eventType = new CustomEvent(eventType as string, {
                detail: data
            });
        }

        const listeners = this.__listeners.get(eventType.type as K);
        if (listeners) {
            listeners.forEach(handler => handler(eventType));
        }
    }
}
